/**
 * Auto table of contents for the article page (all three layouts).
 *
 * - Scans the article body ([data-article-body] .rte h2) and assigns
 *   slugified ids (Greek-friendly, deduplicated).
 * - Populates every [data-article-toc] container on the page. Containers
 *   are rendered empty (and with the `hidden` attribute) in Liquid; the
 *   attribute is removed only once links exist, so no-JS visitors and
 *   articles without h2 headings never see an empty box.
 * - data-layout drives the markup: "magazine" gets pill <a> chips, every
 *   other layout gets <li><a> items appended to [data-toc-list].
 * - Smooth-scrolls anchors (instant for prefers-reduced-motion) and
 *   highlights the active section (IntersectionObserver) in the sticky
 *   sidebar card (data-layout="sidebar").
 */
const body = document.querySelector('[data-article-body]');
const containers = document.querySelectorAll('[data-article-toc]');

const headings = body
  ? [...body.querySelectorAll('.rte h2')].filter(
      (heading) => !heading.closest('[data-article-toc]') && heading.textContent.trim() !== ''
    )
  : [];

if (headings.length && containers.length) {
  const used = new Set();

  headings.forEach((heading, index) => {
    if (!heading.id) {
      const slug =
        heading.textContent
          .trim()
          .toLowerCase()
          .replace(/[^\p{L}\p{N}]+/gu, '-')
          .replace(/^-+|-+$/g, '') || `section-${index + 1}`;
      let candidate = slug;
      let n = 2;
      while (used.has(candidate) || document.getElementById(candidate)) {
        candidate = `${slug}-${n}`;
        n += 1;
      }
      heading.id = candidate;
    }
    used.add(heading.id);
  });

  const sidebarLists = [];

  containers.forEach((container) => {
    const list = container.querySelector('[data-toc-list]') || container;
    const layout = container.dataset.layout || '';

    headings.forEach((heading) => {
      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();

      if (layout === 'magazine') {
        link.className = 'mag-toc__pill';
        list.appendChild(link);
      } else {
        const item = document.createElement('li');
        item.dataset.target = heading.id;
        item.appendChild(link);
        list.appendChild(item);
      }
    });

    container.removeAttribute('hidden');
    if (layout === 'sidebar') sidebarLists.push(list);
  });

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-article-toc] a[href^="#"]');
    if (!link) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    history.pushState(null, '', link.getAttribute('href'));

    /* Collapse the mobile <details> box after choosing a section. */
    const details = link.closest('details');
    if (details) details.open = false;
  });

  /* Active-section highlight — sticky sidebar card only. */
  if (sidebarLists.length && 'IntersectionObserver' in window) {
    const setActive = (id) => {
      sidebarLists.forEach((list) => {
        list.querySelectorAll('li').forEach((item) => {
          item.classList.toggle('is-active', item.dataset.target === id);
        });
      });
    };

    const update = () => {
      let active = headings[0];
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= 140) active = heading;
        else break;
      }
      setActive(active.id);
    };

    const observer = new IntersectionObserver(update, {
      rootMargin: '-120px 0px -55% 0px',
      threshold: 0,
    });
    headings.forEach((heading) => observer.observe(heading));
    update();
  }
}
